import type { DatasetMetric } from "@models/Metric";

/**
 * A widget's desired metric membership after a save/delete — the FULL current
 * deduped set of metric ids it references (a widget can reference several
 * metrics via multiple Metric rows, unlike a Base's single membership).
 * metricIds = [] means the widget references no metrics now (including when
 * the widget itself was deleted).
 */
export interface WidgetMetricMembership {
  widgetId: string;
  dashboardId: string;
  metricIds: string[];
}

/**
 * Applies widget membership changes to a dataset's metrics, keeping each
 * widget in the usedByWidgets array of every metric it currently references.
 * Only *real* changes count: re-saving a widget whose metric membership is
 * unchanged returns `changed: false` so the caller can skip the dataset
 * PATCH. Multiple updates are applied in one pass so a cascade delete
 * produces a single reconciled result.
 */
export function reconcileMetricUsage(
  metrics: DatasetMetric[],
  updates: WidgetMetricMembership[],
): { metrics: DatasetMetric[]; changed: boolean } {
  // Current membership: widgetId -> set of metric ids it currently sits in.
  const currentMetricIdsByWidget = new Map<string, Set<string>>();
  metrics.forEach((m) =>
    (m.usedByWidgets ?? []).forEach((w) => {
      const set = currentMetricIdsByWidget.get(w.widgetId) ?? new Set<string>();
      set.add(m.id);
      currentMetricIdsByWidget.set(w.widgetId, set);
    }),
  );

  // Keep only updates that actually move a widget to a different metric set.
  const effective = updates.filter((u) => {
    const current = currentMetricIdsByWidget.get(u.widgetId) ?? new Set<string>();
    const next = new Set(u.metricIds);
    if (current.size !== next.size) return true;
    for (const id of current) {
      if (!next.has(id)) return true;
    }
    return false;
  });

  if (effective.length === 0) return { metrics, changed: false };

  const next = metrics.map((m) => ({
    ...m,
    usedByWidgets: [...(m.usedByWidgets ?? [])],
  }));

  for (const update of effective) {
    // Remove from wherever it currently lives.
    for (const metric of next) {
      const idx = metric.usedByWidgets.findIndex((w) => w.widgetId === update.widgetId);
      if (idx !== -1) metric.usedByWidgets.splice(idx, 1);
    }
    // Add to every metric this widget now references (deduped by caller,
    // but guard here too — only metrics that still exist get the entry).
    const dedupedIds = new Set(update.metricIds);
    for (const metricId of dedupedIds) {
      const target = next.find((m) => m.id === metricId);
      if (target) {
        target.usedByWidgets.push({
          widgetId: update.widgetId,
          dashboardId: update.dashboardId,
        });
      }
    }
  }

  return { metrics: next, changed: true };
}

/** Total linked visualizations for a metric — the "Linked visualizations" column value. */
export function getLinkedVisualizationCount(metric: DatasetMetric): number {
  return metric.usedByWidgets?.length ?? 0;
}

/** Distinct dashboards a metric is used across — the "Linked dashboards" column value. */
export function getLinkedDashboardCount(metric: DatasetMetric): number {
  return new Set((metric.usedByWidgets ?? []).map((w) => w.dashboardId)).size;
}
